import { type CSSProperties, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  Plus,
  Save,
  Trash2,
  RefreshCw,
  Download,
  Users,
  BrainCircuit,
  CalendarDays,
  Search,
  AlertCircle,
  CheckCircle2,
  Filter,
  Sparkles,
  Clock3,
  MessageSquareText,
  Target,
  TrendingUp,
  Eye,
  ClipboardCheck,
  Layers3,
  ShieldAlert,
  UserRoundCheck,
  Gauge,
  ArrowRight,
  Wand2,
} from 'lucide-react';
import { getAllClients, type ClientItem } from '../services/clientService';
import { SA_AI_CLIENT_EVALUATION_SOURCE, saAiClientEvaluationSeed } from '../data/saAiClientEvaluationSeed';

interface SuiviClientsIAModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type FollowupRow = {
  id?: string;
  clientName: string;
  accountManager: string;
  primaryContact: string;
  contactedBy: string;
  contactDate: string;
  status: string;
  statusDate: string;
  meetingDate: string;
  aiMaturity: string;
  clientInterest: string;
  approachPriority: string;
  expertAssessment: string;
  recommendedAngle: string;
  riskOrCondition: string;
  nextStep: string;
  nextMeetingDate: string;
  demoNeeded: string;
  needsAssessment: string;
  opportunity: string;
  comments: string;
  ddcScore: string;
  ddcBusinessImpact: string;
  ddcDataReadiness: string;
  ddcDecisionAccess: string;
  ddcUseCaseClarity: string;
  ddcRiskLevel: string;
  ddcReasoning: string;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
  deleted?: boolean;
};

type FieldDefinition = {
  key: keyof FollowupRow;
  label: string;
  type?: 'text' | 'date' | 'textarea' | 'select';
  options?: string[];
  placeholder?: string;
};

type FilterKey = 'all' | keyof FollowupRow;

const API_URL = '/api/serq/sa-ai-client-followups';
const RESPONSIBLES_API_URL = `${API_URL}/responsibles`;
const STORAGE_KEY = 'prisme-sa-ai-client-followups-draft-v2';
const RESPONSIBLES_STORAGE_KEY = 'prisme-sa-ai-client-followups-responsibles-v1';

const statusOptions = [
  'À contacter',
  'Contacté',
  'Comité TI prévu',
  'Rencontre IA planifiée',
  'Opportunités proposées',
  'Contrat signé',
  'Fermé — sans suite',
];

const statusLegend = [
  { status: 'À contacter', description: 'Aucune couleur — client à approcher.' },
  { status: 'Contacté', description: 'Aucune couleur — bordure plus forte pour revenir dessus.' },
  { status: 'Comité TI prévu', description: 'Bleu — sujet à traiter au prochain comité TI.' },
  { status: 'Rencontre IA planifiée', description: 'Jaune — rencontre dédiée IA au calendrier.' },
  { status: 'Opportunités proposées', description: 'Orange — pistes ou propositions IA présentées.' },
  { status: 'Contrat signé', description: 'Vert — entente IA conclue.' },
  { status: 'Fermé — sans suite', description: 'Rouge — dossier fermé ou sans suite.' },
];

function normalizeStatus(value: string) {
  if (value === 'Rencontre à planifier') return 'Contacté';
  if (value === 'Rencontre planifiée' || value === 'Rencontré' || value === 'Démo à faire' || value === 'Prise de besoins à faire') return 'Rencontre IA planifiée';
  if (value === 'Opportunité ouverte') return 'Opportunités proposées';
  if (value === 'Pas d’intérêt pour le moment') return 'Fermé — sans suite';
  return statusOptions.includes(value) ? value : 'À contacter';
}

const maturityOptions = [
  'À valider',
  'Aucune initiative IA connue',
  'Utilise déjà l’IA seul',
  'Utilise l’IA avec aide externe',
  'Intéressé mais ne sait pas par où commencer',
  'Veut être accompagné par BZ',
  'Projet IA déjà actif',
];

const interestOptions = ['À valider', 'Faible', 'Moyen', 'Élevé', 'Très élevé', 'Non intéressé'];
const yesNoOptions = ['À valider', 'Oui', 'Non', 'Plus tard'];
const priorityOptions = ['À qualifier', 'Priorité 1 - approche rapide', 'Priorité 2 - approche planifiée', 'Priorité 3 - opportunité à surveiller', 'Hors cible temporaire'];
const ddcScoreOptions = ['À qualifier', 'A - opportunité prioritaire', 'B - potentiel intéressant', 'C - à maturer', 'D - faible potentiel immédiat'];
const ddcReadinessOptions = ['À valider', 'Faible', 'Moyenne', 'Bonne', 'Très bonne'];
const ddcRiskOptions = ['À valider', 'Faible', 'Moyen', 'Élevé', 'Critique'];

const emptyRow = (): FollowupRow => ({
  clientName: '',
  accountManager: '',
  primaryContact: '',
  contactedBy: '',
  contactDate: '',
  status: 'À contacter',
  statusDate: '',
  meetingDate: '',
  aiMaturity: 'À valider',
  clientInterest: 'À valider',
  approachPriority: 'À qualifier',
  expertAssessment: '',
  recommendedAngle: '',
  riskOrCondition: '',
  nextStep: '',
  nextMeetingDate: '',
  demoNeeded: 'À valider',
  needsAssessment: 'À valider',
  opportunity: '',
  comments: '',
  ddcScore: 'À qualifier',
  ddcBusinessImpact: 'À valider',
  ddcDataReadiness: 'À valider',
  ddcDecisionAccess: 'À valider',
  ddcUseCaseClarity: 'À valider',
  ddcRiskLevel: 'À valider',
  ddcReasoning: '',
});

const allFields: FieldDefinition[] = [
  { key: 'clientName', label: 'Client' },
  { key: 'accountManager', label: 'Représentant BZ' },
  { key: 'primaryContact', label: 'Contact client' },
  { key: 'contactedBy', label: 'Qui a parlé au client' },
  { key: 'contactDate', label: 'Date contact' },
  { key: 'status', label: 'Statut suivi' },
  { key: 'statusDate', label: 'Date liée au statut' },
  { key: 'meetingDate', label: 'Date rencontre' },
  { key: 'aiMaturity', label: 'Situation IA actuelle' },
  { key: 'clientInterest', label: 'Intérêt client' },
  { key: 'approachPriority', label: 'Priorité approche' },
  { key: 'ddcScore', label: 'Score client' },
  { key: 'ddcBusinessImpact', label: 'Impact affaires' },
  { key: 'ddcDataReadiness', label: 'Données' },
  { key: 'ddcDecisionAccess', label: 'Accès décideur' },
  { key: 'ddcUseCaseClarity', label: 'Clarté cas d’usage' },
  { key: 'ddcRiskLevel', label: 'Risque' },
  { key: 'ddcReasoning', label: 'Raisonnement client' },
  { key: 'expertAssessment', label: 'Évaluation expert BZ' },
  { key: 'recommendedAngle', label: 'Angle IA recommandé' },
  { key: 'riskOrCondition', label: 'Condition / risque à valider' },
  { key: 'nextStep', label: 'Prochaine action' },
  { key: 'nextMeetingDate', label: 'Prochaine rencontre' },
  { key: 'demoNeeded', label: 'Démo?' },
  { key: 'needsAssessment', label: 'Prise de besoins?' },
  { key: 'opportunity', label: 'Opportunité / besoin' },
  { key: 'comments', label: 'Commentaires' },
];

const filterableFields: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: 'Tous les champs' },
  { key: 'clientName', label: 'Client' },
  { key: 'accountManager', label: 'Représentant BZ' },
  { key: 'primaryContact', label: 'Contact' },
  { key: 'status', label: 'Statut' },
  { key: 'approachPriority', label: 'Priorité' },
  { key: 'ddcScore', label: 'Score client' },
  { key: 'aiMaturity', label: 'Maturité IA' },
  { key: 'nextStep', label: 'Prochaine action' },
  { key: 'comments', label: 'Commentaires' },
];

const sections: Array<{ title: string; subtitle: string; icon: typeof Sparkles; fields: FieldDefinition[] }> = [
  {
    title: 'Identification et représentant BZ',
    subtitle: 'Qui accompagne le client, qui est le contact, où en est le suivi.',
    icon: Users,
    fields: [
      { key: 'clientName', label: 'Client', placeholder: 'Nom du client' },
      { key: 'accountManager', label: 'Représentant BZ', placeholder: 'Nom du représentant BZ' },
      { key: 'primaryContact', label: 'Contact client', placeholder: 'Nom, rôle, courriel, téléphone' },
      { key: 'contactedBy', label: 'Personne qui a parlé au client', placeholder: 'Membre BZ' },
      { key: 'contactDate', label: 'Date du dernier contact', type: 'date' },
      { key: 'status', label: 'Statut', type: 'select', options: statusOptions },
      { key: 'statusDate', label: 'Date liée au statut', type: 'date' },
    ],
  },
  {
    title: 'Évaluation client / valeur IA',
    subtitle: 'Pourquoi ce client mérite une approche IA et quels signaux guident la priorité.',
    icon: ClipboardCheck,
    fields: [
      { key: 'ddcScore', label: 'Score client', type: 'select', options: ddcScoreOptions },
      { key: 'ddcBusinessImpact', label: 'Impact affaires', type: 'select', options: ddcReadinessOptions },
      { key: 'ddcDataReadiness', label: 'Préparation des données', type: 'select', options: ddcReadinessOptions },
      { key: 'ddcDecisionAccess', label: 'Accès au décideur', type: 'select', options: ddcReadinessOptions },
      { key: 'ddcUseCaseClarity', label: 'Clarté du cas d’usage', type: 'select', options: ddcReadinessOptions },
      { key: 'ddcRiskLevel', label: 'Niveau de risque', type: 'select', options: ddcRiskOptions },
      { key: 'ddcReasoning', label: 'Raisonnement / justification', type: 'textarea', placeholder: 'Pourquoi ce score? Signaux d’affaires, données, décideurs, risques, adoption, urgence...' },
    ],
  },
  {
    title: 'Approche IA recommandée',
    subtitle: 'Message d’approche, angle de discussion, risques et conditions à valider.',
    icon: BrainCircuit,
    fields: [
      { key: 'aiMaturity', label: 'Situation IA actuelle', type: 'select', options: maturityOptions },
      { key: 'clientInterest', label: 'Intérêt client', type: 'select', options: interestOptions },
      { key: 'approachPriority', label: 'Priorité d’approche', type: 'select', options: priorityOptions },
      { key: 'expertAssessment', label: 'Évaluation expert BZ', type: 'textarea' },
      { key: 'recommendedAngle', label: 'Angle IA recommandé', type: 'textarea' },
      { key: 'riskOrCondition', label: 'Conditions / risques à valider', type: 'textarea' },
    ],
  },
  {
    title: 'Plan de suivi',
    subtitle: 'Prochaine action claire pour que le dossier avance.',
    icon: CalendarDays,
    fields: [
      { key: 'meetingDate', label: 'Date de rencontre', type: 'date' },
      { key: 'nextStep', label: 'Prochaine action', type: 'textarea', placeholder: 'Ex. planifier rencontre IA, préparer démo, qualifier besoin...' },
      { key: 'nextMeetingDate', label: 'Prochaine rencontre', type: 'date' },
      { key: 'demoNeeded', label: 'Démo à prévoir?', type: 'select', options: yesNoOptions },
      { key: 'needsAssessment', label: 'Prise de besoins?', type: 'select', options: yesNoOptions },
      { key: 'opportunity', label: 'Opportunité / besoin', type: 'textarea' },
      { key: 'comments', label: 'Commentaires internes', type: 'textarea' },
    ],
  },
];

function normalizeRow(row: Partial<FollowupRow>): FollowupRow {
  const normalized = { ...emptyRow(), ...row };
  return { ...normalized, status: normalizeStatus(String(normalized.status || '')) };
}

function normalizeForSearch(value: unknown) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[’']/g, '')
    .toLowerCase()
    .trim();
}

function rowMatchesSearch(row: FollowupRow, query: string, fieldFilter: FilterKey) {
  const normalizedQuery = normalizeForSearch(query);
  if (!normalizedQuery) return true;
  const terms = normalizedQuery.split(/\s+/).filter(Boolean);
  const fieldsToSearch = fieldFilter === 'all' ? allFields.map((field) => field.key) : [fieldFilter];
  const haystack = fieldsToSearch.map((key) => normalizeForSearch(row[key])).join(' | ');
  return terms.every((term) => haystack.includes(term));
}

function scoreFromSignals(row: FollowupRow) {
  const values = [row.ddcBusinessImpact, row.ddcDataReadiness, row.ddcDecisionAccess, row.ddcUseCaseClarity];
  const points = values.reduce((sum, value) => {
    if (value === 'Très bonne') return sum + 4;
    if (value === 'Bonne') return sum + 3;
    if (value === 'Moyenne') return sum + 2;
    if (value === 'Faible') return sum + 1;
    return sum;
  }, 0);
  const riskPenalty = row.ddcRiskLevel === 'Critique' ? 3 : row.ddcRiskLevel === 'Élevé' ? 2 : row.ddcRiskLevel === 'Moyen' ? 1 : 0;
  return Math.max(0, Math.min(100, Math.round(((points / 16) * 100) - riskPenalty * 8)));
}

function buildExpertAssessment(client: ClientItem): Pick<FollowupRow, 'approachPriority' | 'expertAssessment' | 'recommendedAngle' | 'riskOrCondition' | 'nextStep' | 'ddcScore' | 'ddcBusinessImpact' | 'ddcDataReadiness' | 'ddcDecisionAccess' | 'ddcUseCaseClarity' | 'ddcRiskLevel' | 'ddcReasoning'> {
  const size = client.nombre_employes || 0;
  const sector = `${client.secteur || ''} ${client.enjeux_principaux || ''}`.toLowerCase();
  const isOperational = /manufact|production|construction|outil|équipement|distribution|transport|logistique|clinique|santé|cabinet|atelier/.test(sector);
  const isProfessional = /comptable|avocat|notaire|consult|service|administr|municip|association|gestion/.test(sector);
  const isLarger = size >= 25;
  const priority = isLarger || isOperational || isProfessional
    ? 'Priorité 1 - approche rapide'
    : size >= 10
      ? 'Priorité 2 - approche planifiée'
      : 'Priorité 3 - opportunité à surveiller';
  const score = priority.includes('1') ? 'A - opportunité prioritaire' : priority.includes('2') ? 'B - potentiel intéressant' : 'C - à maturer';
  const angle = isOperational
    ? 'Ouvrir avec les irritants opérationnels : suivi des demandes, recherche d’information, procédures internes, automatisation de tâches répétitives et agents simples reliés aux opérations.'
    : isProfessional
      ? 'Ouvrir avec les processus administratifs et le service client : résumé de courriels/documents, préparation de réponses, recherche dans les connaissances internes et suivi des demandes.'
      : 'Ouvrir avec une découverte courte : priorités d’affaires, irritants récurrents, usages actuels de l’IA, qualité des données et opportunités simples à valeur rapide.';

  return {
    approachPriority: priority,
    ddcScore: score,
    ddcBusinessImpact: isLarger || isOperational ? 'Bonne' : 'Moyenne',
    ddcDataReadiness: 'À valider',
    ddcDecisionAccess: 'À valider',
    ddcUseCaseClarity: client.enjeux_principaux ? 'Moyenne' : 'À valider',
    ddcRiskLevel: 'À valider',
    ddcReasoning: 'Raisonnement initial BZ : potentiel à qualifier avec le client. Prioriser la découverte des irritants, des données disponibles, des usages IA non encadrés, du décideur et de la capacité d’adoption avant de proposer une solution.',
    expertAssessment: 'Évaluation de départ : client à qualifier pour identifier les cas d’usage IA concrets, le niveau d’autonomie actuel, les risques de données/confidentialité et la capacité d’adoption. L’objectif est de guider le client vers une première valeur mesurable, pas de vendre une technologie trop tôt.',
    recommendedAngle: angle,
    riskOrCondition: 'À valider : qui utilise déjà l’IA, quelles données sont copiées dans des outils publics, maturité Microsoft 365/Copilot, propriétaire décisionnel, disponibilité des données et besoin de gouvernance minimale.',
    nextStep: 'Planifier un échange de 30 minutes pour qualifier les usages IA actuels, les irritants, les données disponibles et les prochaines actions possibles avec BZ.',
  };
}

function clientToFollowupRow(client: ClientItem): FollowupRow {
  return {
    ...emptyRow(),
    clientName: client.nom,
    opportunity: client.enjeux_principaux || '',
    comments: [client.secteur ? `Secteur : ${client.secteur}` : '', client.taille_entreprise ? `Taille : ${client.taille_entreprise}` : '', client.nombre_employes ? `${client.nombre_employes} employé(s)` : ''].filter(Boolean).join(' | '),
    ...buildExpertAssessment(client),
  };
}

function mergePrismeClientsWithFollowups(existingRows: FollowupRow[], clients: ClientItem[]) {
  const byName = new Map(existingRows.map((row) => [normalizeForSearch(row.clientName), normalizeRow(row)]));
  const usedClientNames = new Set<string>();
  const seededRows = clients
    .filter((client) => client.nom && client.actif !== false && client.archived !== true)
    .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
    .map((client) => byName.get(normalizeForSearch(client.nom)) || clientToFollowupRow(client))
    .filter((row) => {
      const clientKey = normalizeForSearch(row.clientName);
      if (!clientKey || usedClientNames.has(clientKey)) return false;
      usedClientNames.add(clientKey);
      return true;
    });
  const manualRows = existingRows
    .map(normalizeRow)
    .filter((row) => {
      const clientKey = normalizeForSearch(row.clientName);
      if (!clientKey || usedClientNames.has(clientKey)) return false;
      usedClientNames.add(clientKey);
      return true;
    });
  return [...seededRows, ...manualRows];
}

function rowMergeKey(row: Pick<FollowupRow, 'clientName' | 'accountManager'>) {
  return `${normalizeForSearch(row.clientName)}::${normalizeForSearch(row.accountManager)}`;
}

function clientKey(row: Pick<FollowupRow, 'clientName'>) {
  return normalizeForSearch(row.clientName);
}

function mergeExcelSeedWithFollowups(existingRows: FollowupRow[]) {
  const normalizedExistingRows = existingRows.map(normalizeRow);
  const deletedClientKeys = new Set(normalizedExistingRows.filter((row) => row.deleted).map(clientKey));
  const activeExistingRows = normalizedExistingRows.filter((row) => !row.deleted);
  const byClientAndDdc = new Map(activeExistingRows.map((row) => [rowMergeKey(row), normalizeRow(row)]));
  const byClientOnly = new Map(activeExistingRows.map((row) => [clientKey(row), normalizeRow(row)]));
  const usedClientNames = new Set<string>();
  const seededRows = saAiClientEvaluationSeed
    .map((seed) => {
      const normalizedSeed = normalizeRow(seed);
      return byClientAndDdc.get(rowMergeKey(normalizedSeed)) || byClientOnly.get(clientKey(normalizedSeed)) || normalizedSeed;
    })
    .filter((row) => {
      const rowClientKey = clientKey(row);
      if (!rowClientKey || deletedClientKeys.has(rowClientKey) || usedClientNames.has(rowClientKey)) return false;
      usedClientNames.add(rowClientKey);
      return true;
    });
  const manualRows = activeExistingRows
    .map(normalizeRow)
    .filter((row) => {
      const rowClientKey = clientKey(row);
      if (!rowClientKey || deletedClientKeys.has(rowClientKey) || usedClientNames.has(rowClientKey)) return false;
      usedClientNames.add(rowClientKey);
      return true;
    });
  return [...seededRows, ...manualRows];
}

function badgeClass(value: string, type: 'status' | 'priority' | 'score' | 'risk' = 'status') {
  if (type === 'score') {
    if (value.startsWith('A')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (value.startsWith('B')) return 'bg-cyan-50 text-cyan-700 border-cyan-200';
    if (value.startsWith('C')) return 'bg-amber-50 text-amber-700 border-amber-200';
    if (value.startsWith('D')) return 'bg-slate-100 text-slate-600 border-slate-200';
  }
  if (type === 'risk') {
    if (value === 'Critique' || value === 'Élevé') return 'bg-rose-50 text-rose-700 border-rose-200';
    if (value === 'Moyen') return 'bg-amber-50 text-amber-700 border-amber-200';
    if (value === 'Faible') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  }
  if (type === 'priority') {
    if (value.includes('Priorité 1')) return 'bg-rose-50 text-rose-700 border-rose-200';
    if (value.includes('Priorité 2')) return 'bg-amber-50 text-amber-700 border-amber-200';
    if (value.includes('Priorité 3')) return 'bg-blue-50 text-blue-700 border-blue-200';
  }
  if (value === 'À contacter') return 'bg-white text-slate-700 border-slate-200';
  if (value === 'Contacté') return 'bg-white text-slate-900 border-slate-500 ring-2 ring-slate-200';
  if (value === 'Comité TI prévu') return 'bg-blue-50 text-blue-800 border-blue-300';
  if (value === 'Rencontre IA planifiée') return 'bg-yellow-50 text-yellow-800 border-yellow-300';
  if (value === 'Opportunités proposées') return 'bg-orange-50 text-orange-800 border-orange-300';
  if (value === 'Contrat signé') return 'bg-emerald-50 text-emerald-800 border-emerald-300';
  if (value === 'Fermé — sans suite') return 'bg-red-50 text-red-800 border-red-300';
  return 'bg-white text-slate-700 border-slate-200';
}

function statusBorderClass(status: string) {
  if (status === 'Contacté') return 'border-2 border-slate-500';
  if (status === 'Comité TI prévu') return 'border-blue-300';
  if (status === 'Rencontre IA planifiée') return 'border-yellow-300';
  if (status === 'Opportunités proposées') return 'border-orange-300';
  if (status === 'Contrat signé') return 'border-emerald-300';
  if (status === 'Fermé — sans suite') return 'border-red-300';
  return 'border-slate-200';
}

function statusRowClass(status: string) {
  if (status === 'Contacté') return 'bg-white hover:bg-slate-50';
  return '';
}

function statusRowStyle(status: string): CSSProperties {
  if (status === 'Comité TI prévu') return { backgroundColor: '#dbeafe' };
  if (status === 'Rencontre IA planifiée') return { backgroundColor: '#fef9c3' };
  if (status === 'Opportunités proposées') return { backgroundColor: '#ffedd5' };
  if (status === 'Contrat signé') return { backgroundColor: '#d1fae5' };
  if (status === 'Fermé — sans suite') return { backgroundColor: '#fee2e2' };
  return { backgroundColor: '#ffffff' };
}

function statusAccentStyle(status: string): CSSProperties {
  if (status === 'Comité TI prévu') return { backgroundColor: '#60a5fa' };
  if (status === 'Rencontre IA planifiée') return { backgroundColor: '#facc15' };
  if (status === 'Opportunités proposées') return { backgroundColor: '#fb923c' };
  if (status === 'Contrat signé') return { backgroundColor: '#34d399' };
  if (status === 'Fermé — sans suite') return { backgroundColor: '#f87171' };
  if (status === 'Contacté') return { backgroundColor: '#64748b' };
  return { backgroundColor: '#e2e8f0' };
}

function statusTextClass(status: string) {
  return status === 'Contacté' ? 'font-black text-slate-950' : 'font-bold text-slate-950';
}

function statusOptionStyle(status: string): CSSProperties {
  if (status === 'Comité TI prévu') return { backgroundColor: '#dbeafe', color: '#1e40af' };
  if (status === 'Rencontre IA planifiée') return { backgroundColor: '#fef9c3', color: '#854d0e' };
  if (status === 'Opportunités proposées') return { backgroundColor: '#ffedd5', color: '#9a3412' };
  if (status === 'Contrat signé') return { backgroundColor: '#d1fae5', color: '#065f46' };
  if (status === 'Fermé — sans suite') return { backgroundColor: '#fee2e2', color: '#991b1b' };
  return { backgroundColor: '#ffffff', color: '#0f172a' };
}

function statusDateLabel(status: string) {
  if (status === 'Comité TI prévu') return 'Comité TI';
  if (status === 'Rencontre IA planifiée') return 'Rencontre IA';
  if (status === 'Opportunités proposées') return 'Proposition';
  if (status === 'Contrat signé') return 'Signature';
  if (status === 'Fermé — sans suite') return 'Fermeture';
  if (status === 'Contacté') return 'Contact';
  return 'Date';
}

function formatStatusDate(status: string, statusDate: string) {
  if (!statusDate) return '';
  return `${statusDateLabel(status)} : ${statusDate}`;
}

function downloadCsv(rows: FollowupRow[]) {
  const headers = allFields.map((field) => field.label);
  const keys = allFields.map((field) => field.key);
  const escape = (value: unknown) => `"${String(value ?? '').replaceAll('"', '""')}"`;
  const csv = [headers.map(escape).join(','), ...rows.map((row) => keys.map((key) => escape(row[key])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `suivi-clients-ia-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export default function SuiviClientsIAModal({ isOpen, onClose }: SuiviClientsIAModalProps) {
  const [rows, setRows] = useState<FollowupRow[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [fieldFilter, setFieldFilter] = useState<FilterKey>('all');
  const [statusFilter, setStatusFilter] = useState('Tous');
  const [priorityFilter, setPriorityFilter] = useState('Toutes');
  const [scoreFilter, setScoreFilter] = useState('Tous');
  const [responsibleFilter, setResponsibleFilter] = useState('Tous');
  const [customResponsibles, setCustomResponsibles] = useState<string[]>([]);
  const [newResponsibleName, setNewResponsibleName] = useState('');
  const [viewMode, setViewMode] = useState<'focus' | 'compact'>('compact');
  const [selectedRepresentative, setSelectedRepresentative] = useState<string | null>(null);
  const [bulkRepresentativeName, setBulkRepresentativeName] = useState('');
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  const selectedRow = selectedIndex !== null ? rows[selectedIndex] : null;

  const responsibleOptions = useMemo(() => {
    const names = [...rows.map((row) => row.accountManager), ...customResponsibles]
      .map((name) => name.trim())
      .filter(Boolean);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [customResponsibles, rows]);

  const filteredRows = useMemo(() => {
    return rows
      .filter((row) => {
        const matchesSearch = rowMatchesSearch(row, query, fieldFilter);
        const matchesStatus = statusFilter === 'Tous' || row.status === statusFilter;
        const matchesPriority = priorityFilter === 'Toutes' || row.approachPriority === priorityFilter;
        const matchesScore = scoreFilter === 'Tous' || row.ddcScore === scoreFilter;
        const matchesResponsible = responsibleFilter === 'Tous' || normalizeForSearch(row.accountManager) === normalizeForSearch(responsibleFilter);
        return matchesSearch && matchesStatus && matchesPriority && matchesScore && matchesResponsible;
      })
      .sort((a, b) => (a.clientName || '').localeCompare(b.clientName || '', 'fr', { sensitivity: 'base' }));
  }, [fieldFilter, priorityFilter, query, responsibleFilter, rows, scoreFilter, statusFilter]);

  const metrics = useMemo(() => {
    const total = rows.length;
    const contacted = rows.filter((row) => row.status !== 'À contacter').length;
    const opportunities = rows.filter((row) => row.status === 'Opportunités proposées' || row.status === 'Contrat signé' || row.ddcScore.startsWith('A')).length;
    const demos = rows.filter((row) => row.demoNeeded === 'Oui').length;
    const avgScore = rows.length ? Math.round(rows.reduce((sum, row) => sum + scoreFromSignals(row), 0) / rows.length) : 0;
    return { total, contacted, opportunities, demos, avgScore };
  }, [rows]);

  const priorityOverview = useMemo(() => {
    const priority1 = rows.filter((row) => row.approachPriority.includes('Priorité 1') || row.ddcScore.startsWith('A'));
    const toPlan = rows.filter((row) => row.status === 'Comité TI prévu' || row.status === 'Rencontre IA planifiée');
    const noOwner = rows.filter((row) => !row.accountManager.trim());
    const demosNeeded = rows.filter((row) => row.demoNeeded === 'Oui');
    return [
      { label: 'Approche rapide', count: priority1.length, detail: 'Priorité 1 ou score A' },
      { label: 'Rencontres', count: toPlan.length, detail: 'à planifier ou planifiées' },
      { label: 'Démos', count: demosNeeded.length, detail: 'démos à prévoir' },
      { label: 'Sans représentant', count: noOwner.length, detail: 'à attribuer' },
    ];
  }, [rows]);

  const prioritiesByResponsible = useMemo(() => {
    const groups = new Map<string, FollowupRow[]>();
    rows.forEach((row) => {
      const responsible = row.accountManager.trim() || 'À attribuer';
      groups.set(responsible, [...(groups.get(responsible) || []), row]);
    });
    responsibleOptions.forEach((responsible) => {
      if (!groups.has(responsible)) groups.set(responsible, []);
    });
    return Array.from(groups.entries())
      .map(([responsible, items]) => ({
        responsible,
        total: items.length,
        p1: items.filter((row) => row.approachPriority.includes('Priorité 1') || row.ddcScore.startsWith('A')).length,
        p2: items.filter((row) => row.approachPriority.includes('Priorité 2') || row.ddcScore.startsWith('B')).length,
        demos: items.filter((row) => row.demoNeeded === 'Oui').length,
        next: items.filter((row) => row.status === 'Comité TI prévu' || row.status === 'Rencontre IA planifiée' || row.status === 'Opportunités proposées').length,
        clients: [...items].sort((a, b) => a.clientName.localeCompare(b.clientName, 'fr', { sensitivity: 'base' })),
      }))
      .sort((a, b) => a.responsible.localeCompare(b.responsible, 'fr', { sensitivity: 'base' }));
  }, [responsibleOptions, rows]);

  const selectedRepresentativeSummary = useMemo(() => {
    if (!selectedRepresentative) return null;
    return prioritiesByResponsible.find((item) => item.responsible === selectedRepresentative) || null;
  }, [prioritiesByResponsible, selectedRepresentative]);

  const openRepresentativeCard = (representative: string) => {
    setSelectedRepresentative(representative);
    setBulkRepresentativeName(representative === 'À attribuer' ? '' : representative);
  };

  const closeRepresentativeCard = () => {
    setSelectedRepresentative(null);
    setBulkRepresentativeName('');
  };

  const loadRows = async () => {
    setLoading(true);
    setError(null);
    try {
      const followupResponse = await fetch(API_URL, { credentials: 'include' });
      if (!followupResponse.ok) throw new Error('API non disponible');
      const data = await followupResponse.json();
      const loadedRows = (data.rows || []).map(normalizeRow);
      const nextRows = mergeExcelSeedWithFollowups(loadedRows);
      setRows(nextRows);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(nextRows));
    } catch {
      const draft = localStorage.getItem(STORAGE_KEY) || localStorage.getItem('prisme-sa-ai-client-followups-draft-v1');
      const draftRows = draft ? JSON.parse(draft).map(normalizeRow) : [];
      const nextRows = mergeExcelSeedWithFollowups(draftRows);
      setRows(nextRows);
      setError('Mode brouillon local : les données serveur ne sont pas disponibles pour le moment.');
    } finally {
      setLoading(false);
    }
  };

  const loadResponsibles = async () => {
    try {
      const response = await fetch(RESPONSIBLES_API_URL, { credentials: 'include' });
      if (!response.ok) throw new Error('API représentants non disponible');
      const data = await response.json();
      const names: string[] = (data.responsibles || [])
        .map((responsible: { name?: string }) => String(responsible.name || '').trim())
        .filter(Boolean);
      const uniqueNames = Array.from(new Set<string>(names)).sort((a, b) => a.localeCompare(b, 'fr'));
      setCustomResponsibles(uniqueNames);
      localStorage.setItem(RESPONSIBLES_STORAGE_KEY, JSON.stringify(uniqueNames));
    } catch {
      try {
        const stored = localStorage.getItem(RESPONSIBLES_STORAGE_KEY);
        if (stored) setCustomResponsibles(JSON.parse(stored));
      } catch {
        // Ignore les erreurs de stockage local.
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadRows();
      loadResponsibles();
    }
  }, [isOpen]);

  useEffect(() => {
    localStorage.setItem(RESPONSIBLES_STORAGE_KEY, JSON.stringify(customResponsibles));
  }, [customResponsibles]);

  useEffect(() => {
    if (rows.length) localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
    if (selectedIndex !== null && selectedIndex > rows.length - 1) setSelectedIndex(null);
  }, [rows, selectedIndex]);

  const updateField = (key: keyof FollowupRow, value: string) => {
    if (selectedIndex === null) return;
    setRows((current) => current.map((row, index) => (index === selectedIndex ? { ...row, [key]: value } : row)));
  };

  const addClient = () => {
    const newRow = { ...emptyRow(), clientName: 'Nouveau client', ddcReasoning: 'À compléter : indiquer le potentiel, les irritants, le décideur, les données disponibles et la prochaine action.' };
    setRows((current) => [newRow, ...current]);
    setSelectedIndex(0);
    setQuery('');
    setStatusFilter('Tous');
    setPriorityFilter('Toutes');
    setScoreFilter('Tous');
  };

  const resetSearchAndFilters = () => {
    setQuery('');
    setFieldFilter('all');
    setStatusFilter('Tous');
    setPriorityFilter('Toutes');
    setScoreFilter('Tous');
    setResponsibleFilter('Tous');
  };

  const addResponsible = async () => {
    const name = newResponsibleName.trim();
    if (!name) return;
    if (responsibleOptions.some((existing) => normalizeForSearch(existing) === normalizeForSearch(name))) {
      setError('Ce représentant BZ existe déjà.');
      return;
    }
    setCustomResponsibles((current) => [...current, name].sort((a, b) => a.localeCompare(b, 'fr')));
    setNewResponsibleName('');
    setError(null);
    try {
      const response = await fetch(RESPONSIBLES_API_URL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!response.ok) throw new Error('Sauvegarde représentant impossible');
      await loadResponsibles();
      setLastSavedAt(new Date().toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' }));
    } catch {
      setError('Représentant BZ ajouté localement, mais la sauvegarde serveur devra être revérifiée.');
    }
  };

  const removeResponsible = async (name: string) => {
    const impactedRows = rows.filter((row) => normalizeForSearch(row.accountManager) === normalizeForSearch(name));
    if (impactedRows.length) {
      setError(`Impossible de retirer « ${name} » : ${impactedRows.length} client(s) sont encore associés à ce représentant BZ. Réattribue les clients concernés avant de retirer le nom de la liste.`);
      setResponsibleFilter(name);
      return;
    }
    if (!window.confirm(`Retirer le représentant BZ « ${name} » de la liste?`)) return;
    setCustomResponsibles((current) => current.filter((item) => normalizeForSearch(item) !== normalizeForSearch(name)));
    if (normalizeForSearch(responsibleFilter) === normalizeForSearch(name)) setResponsibleFilter('Tous');
    try {
      const response = await fetch(`${RESPONSIBLES_API_URL}/${encodeURIComponent(name)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Retrait représentant impossible');
      setLastSavedAt(new Date().toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' }));
      setError(null);
    } catch {
      setError('Représentant BZ retiré localement, mais la sauvegarde serveur devra être revérifiée.');
    }
  };

  const syncPrismeClients = async () => {
    setLoading(true);
    setError(null);
    try {
      const clients = await getAllClients();
      setRows((current) => {
        const withExcelSeed = mergeExcelSeedWithFollowups(current.map(normalizeRow));
        return mergePrismeClientsWithFollowups(withExcelSeed, clients);
      });
    } catch {
      setError('Impossible de synchroniser la liste de clients PRIsME pour le moment.');
    } finally {
      setLoading(false);
    }
  };

  const persistRow = async (row: FollowupRow) => {
    const method = row.id ? 'PUT' : 'POST';
    const url = row.id ? `${API_URL}/${row.id}` : API_URL;
    const { id, createdAt, updatedAt, createdBy, updatedBy, ...payload } = row as FollowupRow & Record<string, string | boolean | undefined>;
    const response = await fetch(url, {
      method,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: payload }),
    });
    if (!response.ok) throw new Error('Sauvegarde impossible');
    const data = await response.json();
    return normalizeRow(data.row);
  };

  const reassignSelectedRepresentativeClients = async () => {
    if (!selectedRepresentativeSummary) return;
    const name = bulkRepresentativeName.trim();
    if (!name) {
      setError('Indique le nom du représentant BZ qui doit reprendre ces clients.');
      return;
    }
    if (name === 'À attribuer') {
      setError('Choisis un vrai nom de représentant BZ, pas « À attribuer ».');
      return;
    }
    const clientsToUpdate = selectedRepresentativeSummary.clients;
    if (!clientsToUpdate.length) return;
    if (!window.confirm(`Attribuer ${clientsToUpdate.length} client(s) à « ${name} »?`)) return;

    setSavingId(`bulk-${selectedRepresentativeSummary.responsible}`);
    setError(null);
    const updatedRows = rows.map((row) => (
      clientsToUpdate.includes(row) ? { ...row, accountManager: name } : row
    ));
    setRows(updatedRows);
    setCustomResponsibles((current) => Array.from(new Set([...current, name])).sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' })));

    try {
      await fetch(RESPONSIBLES_API_URL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      await Promise.all(clientsToUpdate.map((row) => persistRow({ ...row, accountManager: name })));
      setLastSavedAt(new Date().toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' }));
      setSelectedRepresentative(name);
      setBulkRepresentativeName(name);
    } catch {
      setError('Les clients sont réattribués localement, mais la sauvegarde serveur devra être revérifiée.');
    } finally {
      setSavingId(null);
    }
  };

  const saveRow = async (index: number | null = selectedIndex) => {
    if (index === null) return;
    const row = rows[index];
    if (!row) return;
    if (!row.clientName.trim()) {
      setError('Le nom du client est obligatoire avant de sauvegarder.');
      return;
    }
    setSavingId(row.id || `new-${index}`);
    setError(null);
    try {
      const savedRow = await persistRow(row);
      setRows((current) => current.map((item, rowIndex) => (rowIndex === index ? savedRow : item)));
      setLastSavedAt(new Date().toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' }));
    } catch {
      setError('Sauvegarde serveur impossible. Les changements restent en brouillon local.');
    } finally {
      setSavingId(null);
    }
  };

  const deleteRow = async (index: number | null = selectedIndex) => {
    if (index === null) return;
    const row = rows[index];
    if (!row) return;
    if (!window.confirm(`Supprimer le client « ${row.clientName || 'sans nom'} » du suivi IA?`)) return;
    const tombstone = { ...row, deleted: true };
    setRows((current) => current.filter((_, rowIndex) => rowIndex !== index));
    setSelectedIndex(null);
    try {
      await persistRow(tombstone);
      setLastSavedAt(new Date().toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' }));
    } catch {
      setError('Le client est retiré localement, mais la suppression serveur devra être revérifiée.');
    }
  };

  const renderField = (field: FieldDefinition) => {
    if (!selectedRow) return null;
    const value = String(selectedRow[field.key] || '');
    const baseClass = 'w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100';
    if (field.key === 'accountManager') {
      return (
        <div className="space-y-2">
          <select value={value} onChange={(event) => updateField(field.key, event.target.value)} className={baseClass}>
            <option value="">À attribuer</option>
            {responsibleOptions.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
          <input value={value} onChange={(event) => updateField(field.key, event.target.value)} placeholder="Ou inscrire un autre représentant BZ" className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100" />
        </div>
      );
    }
    if (field.type === 'select') {
      return (
        <select value={value} onChange={(event) => updateField(field.key, event.target.value)} className={`${baseClass} ${field.key === 'status' ? badgeClass(value) : ''}`}>
          {(field.options || []).map((option) => <option key={option} style={field.key === 'status' ? statusOptionStyle(option) : undefined}>{option}</option>)}
        </select>
      );
    }
    if (field.type === 'textarea') {
      return <textarea value={value} onChange={(event) => updateField(field.key, event.target.value)} placeholder={field.placeholder} className={`${baseClass} min-h-[118px] resize-y leading-relaxed`} />;
    }
    return <input type={field.type || 'text'} value={value} onChange={(event) => updateField(field.key, event.target.value)} placeholder={field.placeholder} className={baseClass} />;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-md" onClick={onClose}>
          <motion.div initial={{ opacity: 0, scale: 0.96, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.98, y: 8 }} transition={{ duration: 0.2 }} onClick={(event) => event.stopPropagation()} className="max-h-[94vh] w-full max-w-[98vw] overflow-hidden rounded-[2rem] border border-white/20 bg-white shadow-2xl">
            <div className="relative overflow-hidden bg-slate-950 px-7 py-6 text-white">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(34,211,238,0.32),transparent_32%),radial-gradient(circle_at_80%_10%,rgba(168,85,247,0.24),transparent_30%),linear-gradient(135deg,#020617,#0f172a_58%,#083344)]" />
              <div className="relative flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                <div>
                  <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-cyan-300/40 bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-[0.22em] text-cyan-100 backdrop-blur"><Wand2 className="h-3.5 w-3.5" /> Démarche IA clients BZ</div>
                  <h2 className="text-4xl font-black tracking-tight">Suivi clients IA</h2>
                  <p className="mt-2 max-w-4xl text-sm leading-relaxed text-slate-200">Une vue de pilotage pour savoir qui approcher, pourquoi, avec quel angle IA, et quelle prochaine action doit faire avancer le dossier.</p>
                </div>
                <div className="flex shrink-0 flex-nowrap items-center justify-end gap-2 overflow-x-auto pb-1">
                  <button onClick={addClient} className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-cyan-400 px-4 py-2 text-sm font-black text-slate-950 shadow-lg shadow-cyan-500/20 hover:bg-cyan-300" type="button"><Plus className="h-4 w-4" /> Ajouter</button>
                  <button onClick={syncPrismeClients} disabled={loading} className="inline-flex shrink-0 items-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-4 py-2 text-sm font-bold text-white backdrop-blur hover:bg-white/15 disabled:opacity-50" type="button"><Users className="h-4 w-4" /> Sync PRIsME</button>
                  <button onClick={loadRows} className="inline-flex shrink-0 items-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-4 py-2 text-sm font-bold text-white backdrop-blur hover:bg-white/15" type="button"><RefreshCw className="h-4 w-4" /> Recharger</button>
                  <button onClick={() => downloadCsv(rows)} className="inline-flex shrink-0 items-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-4 py-2 text-sm font-bold text-white backdrop-blur hover:bg-white/15" type="button"><Download className="h-4 w-4" /> Export</button>
                  <button onClick={onClose} aria-label="Fermer" className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-white/10 text-white hover:bg-white/15" type="button"><X className="h-5 w-5" /></button>
                </div>
              </div>
            </div>

            <div className="max-h-[calc(94vh-140px)] overflow-y-auto bg-slate-50 p-6">
              <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-5">
                {[
                  { label: 'clients', value: metrics.total, icon: Users, style: 'from-cyan-500 to-blue-600' },
                  { label: 'contactés', value: metrics.contacted, icon: CheckCircle2, style: 'from-emerald-500 to-teal-600' },
                  { label: 'opportunités', value: metrics.opportunities, icon: TrendingUp, style: 'from-violet-500 to-fuchsia-600' },
                  { label: 'démos', value: metrics.demos, icon: CalendarDays, style: 'from-amber-500 to-orange-600' },
                  { label: 'score moyen', value: `${metrics.avgScore}%`, icon: Gauge, style: 'from-slate-700 to-slate-950' },
                ].map((metric) => {
                  const Icon = metric.icon;
                  return (
                    <div key={metric.label} className="overflow-hidden rounded-3xl border border-white bg-white shadow-sm">
                      <div className={`bg-gradient-to-br ${metric.style} p-4 text-white`}><Icon className="mb-4 h-6 w-6 opacity-90" /><p className="text-3xl font-black">{metric.value}</p><p className="text-xs font-bold uppercase tracking-wider opacity-80">{metric.label}</p></div>
                    </div>
                  );
                })}
              </div>

              <div className="mb-5 rounded-3xl border border-cyan-200 bg-cyan-50 p-4 text-sm text-cyan-950 shadow-sm">
                <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                  <div className="font-black">Source clients : fichier Excel Évaluation clients BZ</div>
                  <div className="font-bold">{SA_AI_CLIENT_EVALUATION_SOURCE.extractedRows} lignes clients extraites / {SA_AI_CLIENT_EVALUATION_SOURCE.expectedTotalFromSummary} indiquées dans le résumé Excel</div>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-cyan-800">
                  Les onglets lisibles du fichier contiennent {SA_AI_CLIENT_EVALUATION_SOURCE.extractedRows} clients. Les lignes manquantes proviennent du résumé mais ne sont pas présentes dans les onglets du fichier fourni.
                </p>
              </div>

              <div className="mb-5 grid grid-cols-1 gap-4 xl:grid-cols-[1fr_1.4fr]">
                <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-black text-slate-950">Priorités d’ensemble</h3>
                      <p className="text-xs font-semibold text-slate-500">Vue rapide pour piloter l’approche IA.</p>
                    </div>
                    <Target className="h-6 w-6 text-cyan-700" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {priorityOverview.map((item) => (
                      <button key={item.label} type="button" onClick={() => item.label === 'Approche rapide' ? setPriorityFilter('Priorité 1 - approche rapide') : item.label === 'Démos' ? setScoreFilter('Tous') : undefined} className="rounded-2xl border border-slate-100 bg-slate-50 p-3 text-left hover:border-cyan-200 hover:bg-cyan-50">
                        <p className="text-2xl font-black text-slate-950">{item.count}</p>
                        <p className="text-xs font-black uppercase tracking-wide text-slate-600">{item.label}</p>
                        <p className="mt-1 text-[11px] text-slate-500">{item.detail}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <h3 className="text-lg font-black text-slate-950">Priorités par représentant BZ</h3>
                      <p className="text-xs font-semibold text-slate-500">Représentant BZ, volume, priorités et actions à venir.</p>
                    </div>
                    <div className="flex gap-2">
                      <input value={newResponsibleName} onChange={(event) => setNewResponsibleName(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') addResponsible(); }} placeholder="Ajouter représentant BZ" className="w-44 rounded-2xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100" />
                      <button onClick={addResponsible} disabled={!newResponsibleName.trim()} className="rounded-2xl bg-slate-950 px-3 py-2 text-sm font-black text-white hover:bg-cyan-800 disabled:cursor-not-allowed disabled:opacity-40" type="button">Ajouter</button>
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto rounded-2xl border border-slate-100">
                    {prioritiesByResponsible.map((item) => (
                      <div key={item.responsible} className="grid grid-cols-[1.2fr_repeat(5,70px)_80px] items-center gap-2 border-b border-slate-100 px-3 py-2 text-sm last:border-b-0">
                        <button type="button" onClick={() => openRepresentativeCard(item.responsible)} className="text-left font-black text-slate-900 hover:text-cyan-700">{item.responsible}</button>
                        <span className="text-center font-bold text-slate-600">{item.total}</span>
                        <span className="text-center font-bold text-rose-600">P1 {item.p1}</span>
                        <span className="text-center font-bold text-amber-600">P2 {item.p2}</span>
                        <span className="text-center font-bold text-violet-600">Demo {item.demos}</span>
                        <span className="text-center font-bold text-cyan-700">Next {item.next}</span>
                        <button onClick={() => removeResponsible(item.responsible)} className="rounded-xl border border-rose-100 px-2 py-1 text-xs font-bold text-rose-700 hover:bg-rose-50" type="button">Suppr.</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mb-5 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <h3 className="text-sm font-black uppercase tracking-wide text-slate-800">Recherche, filtres et affichage</h3>
                    <p className="text-xs font-semibold text-slate-500">Liste triée par nom de client. Utilise le filtre représentant BZ au besoin.</p>
                  </div>
                  <button onClick={() => setViewMode(viewMode === 'focus' ? 'compact' : 'focus')} className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 px-4 py-3 text-sm font-black text-slate-700 hover:bg-slate-50" type="button"><Filter className="h-4 w-4" /> {viewMode === 'focus' ? 'Vue compacte' : 'Vue cartes'}</button>
                </div>
                <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1.4fr_170px_190px_190px_210px_190px]">
                  <label className="block">
                    <span className="mb-1 block text-[11px] font-black uppercase tracking-wide text-slate-500">Recherche</span>
                    <div className="flex items-center gap-2 rounded-2xl border border-slate-200 px-4 py-3">
                      <Search className="h-5 w-5 text-slate-400" />
                      <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Client, notes, représentant BZ, priorité, prochaine action..." className="w-full border-0 text-sm outline-none" />
                    </div>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-[11px] font-black uppercase tracking-wide text-slate-500">Champ recherché</span>
                    <select value={fieldFilter} onChange={(event) => setFieldFilter(event.target.value as FilterKey)} className="w-full rounded-2xl border border-slate-200 px-3 py-3 text-sm outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100">
                      {filterableFields.map((field) => <option key={field.key} value={field.key}>{field.label}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-[11px] font-black uppercase tracking-wide text-slate-500">Représentant BZ</span>
                    <select value={responsibleFilter} onChange={(event) => setResponsibleFilter(event.target.value)} className="w-full rounded-2xl border border-slate-200 px-3 py-3 text-sm outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100">
                      <option value="Tous">Tous les représentants</option>
                      {responsibleOptions.map((name) => <option key={name} value={name}>{name}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-[11px] font-black uppercase tracking-wide text-slate-500">Statut</span>
                    <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100 ${statusFilter !== 'Tous' ? badgeClass(statusFilter) : 'border-slate-200 bg-white text-slate-800'}`}><option value="Tous">Tous les statuts</option>{statusOptions.map((option) => <option key={option} style={statusOptionStyle(option)}>{option}</option>)}</select>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-[11px] font-black uppercase tracking-wide text-slate-500">Priorité approche</span>
                    <select value={priorityFilter} onChange={(event) => setPriorityFilter(event.target.value)} className="w-full rounded-2xl border border-slate-200 px-3 py-3 text-sm outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100"><option value="Toutes">Toutes les priorités</option>{priorityOptions.map((option) => <option key={option}>{option}</option>)}</select>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-[11px] font-black uppercase tracking-wide text-slate-500">Score client</span>
                    <select value={scoreFilter} onChange={(event) => setScoreFilter(event.target.value)} className="w-full rounded-2xl border border-slate-200 px-3 py-3 text-sm outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100"><option value="Tous">Tous les scores</option>{ddcScoreOptions.map((option) => <option key={option}>{option}</option>)}</select>
                  </label>
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs font-bold text-slate-500">
                  <span>{filteredRows.length} client(s) affiché(s) sur {rows.length}</span>
                  {(query || fieldFilter !== 'all' || responsibleFilter !== 'Tous' || statusFilter !== 'Tous' || priorityFilter !== 'Toutes' || scoreFilter !== 'Tous') && (
                    <button onClick={resetSearchAndFilters} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-slate-700 hover:bg-slate-100" type="button">
                      Effacer recherche et filtres
                    </button>
                  )}
                </div>
                {error && <div className="mt-3 flex items-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800"><AlertCircle className="h-4 w-4" />{error}</div>}
                {lastSavedAt && <p className="mt-3 text-xs font-bold text-emerald-700">Dernière sauvegarde : {lastSavedAt}</p>}
              </div>

              {viewMode === 'focus' ? (
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-3 2xl:grid-cols-4">
                  {filteredRows.map((row) => {
                    const index = rows.indexOf(row);
                    const ddcSignal = scoreFromSignals(row);
                    return (
                      <button key={row.id || `draft-${index}`} type="button" onClick={() => setSelectedIndex(index)} style={statusRowStyle(row.status)} className={`group overflow-hidden rounded-3xl border text-left shadow-sm transition hover:-translate-y-0.5 hover:border-cyan-300 hover:shadow-xl ${statusRowClass(row.status)} ${statusBorderClass(row.status)}`}>
                        <div className="h-2" style={statusAccentStyle(row.status)} />
                        <div className="p-5">
                          <div className="mb-3 flex items-start justify-between gap-3">
                            <div>
                              <p className="text-lg font-black text-slate-950 group-hover:text-cyan-800">{row.clientName || 'Client sans nom'}</p>
                              <p className="mt-1 text-xs font-semibold text-slate-500">{row.accountManager || 'Représentant à attribuer'}</p>
                            </div>
                            <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-black ${badgeClass(row.ddcScore, 'score')}`}>{row.ddcScore.replace(' - ', ' · ')}</span>
                          </div>
                          <div className="mb-4 flex flex-wrap gap-2">
                            <div className="flex flex-col gap-1">
                              <span className={`text-xs ${statusTextClass(row.status)}`}>{row.status}</span>
                              {row.statusDate && <span className="text-[11px] font-bold text-slate-500">{formatStatusDate(row.status, row.statusDate)}</span>}
                            </div>
                            <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${badgeClass(row.approachPriority, 'priority')}`}>{row.approachPriority.replace('Priorité ', 'P')}</span>
                            <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${badgeClass(row.ddcRiskLevel, 'risk')}`}>Risque {row.ddcRiskLevel}</span>
                          </div>
                          <div className="mb-4 rounded-2xl bg-slate-50 p-3">
                            <div className="mb-2 flex items-center justify-between text-xs font-black text-slate-600"><span>Signal client</span><span>{ddcSignal}%</span></div>
                            <div className="h-2 overflow-hidden rounded-full bg-slate-200"><div className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-emerald-500" style={{ width: `${ddcSignal}%` }} /></div>
                          </div>
                          <p className="line-clamp-3 min-h-[54px] text-sm leading-relaxed text-slate-600">{row.nextStep || row.recommendedAngle || 'Aucune prochaine action inscrite.'}</p>
                          <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4 text-xs font-bold text-slate-500">
                            <span className="inline-flex items-center gap-1"><Clock3 className="h-3.5 w-3.5" /> {row.nextMeetingDate || 'À planifier'}</span>
                            <span className="inline-flex items-center gap-1 text-cyan-700">Ouvrir <ArrowRight className="h-3.5 w-3.5" /></span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                  <div className="grid grid-cols-[minmax(220px,1.5fr)_160px_180px_160px_minmax(260px,2fr)_120px] gap-3 border-b border-slate-100 bg-slate-100/80 px-4 py-3 text-xs font-black uppercase tracking-wide text-slate-500">
                    <span>Client</span><span>Statut</span><span>Score client</span><span>Représentant BZ</span><span>Prochaine action</span><span>Action</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {filteredRows.map((row) => {
                      const index = rows.indexOf(row);
                      return (
                        <button key={row.id || `compact-${index}`} type="button" onClick={() => setSelectedIndex(index)} style={statusRowStyle(row.status)} className={`grid w-full grid-cols-[minmax(220px,1.5fr)_160px_180px_160px_minmax(260px,2fr)_120px] gap-3 border-l-4 px-4 py-3 text-left text-sm transition ${statusRowClass(row.status)} ${statusBorderClass(row.status)}`}>
                          <span className="font-black text-slate-950">{row.clientName}</span>
                          <span className="flex flex-col items-start gap-1"><span className={`text-xs ${statusTextClass(row.status)}`}>{row.status}</span>{row.statusDate && <span className="text-[11px] font-bold text-slate-500">{formatStatusDate(row.status, row.statusDate)}</span>}</span>
                          <span><span className={`rounded-full border px-2 py-1 text-[11px] font-bold ${badgeClass(row.ddcScore, 'score')}`}>{row.ddcScore}</span></span>
                          <span className="text-slate-600">{row.accountManager || '—'}</span>
                          <span className="line-clamp-2 text-slate-600">{row.nextStep || '—'}</span>
                          <span className="font-bold text-cyan-700">Voir fiche</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-5 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <h3 className="text-sm font-black uppercase tracking-wide text-slate-800">Légende des statuts</h3>
                    <p className="text-xs font-semibold text-slate-500">La couleur de la ligne ou de la carte indique le statut. Le texte du statut reste noir pour garder la lecture simple.</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {statusLegend.map((item) => (
                    <div key={item.status} className="rounded-2xl border px-3 py-2" style={statusRowStyle(item.status)}>
                      <p className="text-xs font-black text-slate-950">{item.status}</p>
                      <p className="mt-0.5 max-w-[190px] text-[10px] font-semibold text-slate-600">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {!filteredRows.length && (
                <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
                  <p className="text-lg font-black text-slate-800">Aucun client trouvé avec la recherche ou les filtres actuels.</p>
                  <p className="mt-2 text-sm">Essaie d’effacer les filtres, de chercher seulement une partie du nom, ou de synchroniser/importer la liste complète des clients.</p>
                  <button onClick={resetSearchAndFilters} className="mt-4 rounded-2xl bg-cyan-700 px-4 py-2 text-sm font-black text-white hover:bg-cyan-800" type="button">
                    Effacer recherche et filtres
                  </button>
                </div>
              )}
            </div>
          </motion.div>

          <AnimatePresence>
            {selectedRepresentativeSummary && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[108] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm" onClick={closeRepresentativeCard}>
                <motion.div initial={{ opacity: 0, scale: 0.96, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97, y: 10 }} onClick={(event) => event.stopPropagation()} className="max-h-[86vh] w-full max-w-3xl overflow-hidden rounded-[2rem] bg-white shadow-2xl">
                  <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-gradient-to-br from-slate-950 to-cyan-950 p-6 text-white">
                    <div>
                      <p className="mb-2 text-xs font-black uppercase tracking-[0.22em] text-cyan-200">Représentant BZ</p>
                      <h3 className="text-3xl font-black">{selectedRepresentativeSummary.responsible}</h3>
                      <p className="mt-2 text-sm font-semibold text-slate-200">{selectedRepresentativeSummary.total} client(s), triés en ordre alphabétique.</p>
                    </div>
                    <button onClick={closeRepresentativeCard} aria-label="Fermer la fiche représentant BZ" className="rounded-2xl border border-white/20 bg-white/10 p-2 text-white hover:bg-white/15" type="button"><X className="h-6 w-6" /></button>
                  </div>
                  <div className="max-h-[calc(86vh-130px)] overflow-y-auto bg-slate-50 p-5">
                    <div className="mb-4 grid grid-cols-4 gap-3">
                      {[
                        { label: 'Clients', value: selectedRepresentativeSummary.total, style: 'text-slate-950' },
                        { label: 'P1', value: selectedRepresentativeSummary.p1, style: 'text-rose-600' },
                        { label: 'P2', value: selectedRepresentativeSummary.p2, style: 'text-amber-600' },
                        { label: 'Next', value: selectedRepresentativeSummary.next, style: 'text-cyan-700' },
                      ].map((metric) => (
                        <div key={metric.label} className="rounded-2xl border border-slate-200 bg-white p-3 text-center shadow-sm">
                          <p className={`text-2xl font-black ${metric.style}`}>{metric.value}</p>
                          <p className="text-xs font-black uppercase tracking-wide text-slate-500">{metric.label}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mb-4 rounded-3xl border border-cyan-100 bg-white p-4 shadow-sm">
                      <div className="mb-3">
                        <p className="text-sm font-black text-slate-950">Réattribuer les clients de cette liste</p>
                        <p className="text-xs font-semibold text-slate-500">Utile si un nom a été retiré avant de transférer ses clients à quelqu’un d’autre.</p>
                      </div>
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <input list="representants-bz-options" value={bulkRepresentativeName} onChange={(event) => setBulkRepresentativeName(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') reassignSelectedRepresentativeClients(); }} placeholder="Nom du représentant BZ, ex. Edita" className="min-w-0 flex-1 rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100" />
                        <datalist id="representants-bz-options">
                          {responsibleOptions.map((name) => <option key={name} value={name} />)}
                        </datalist>
                        <button onClick={reassignSelectedRepresentativeClients} disabled={!bulkRepresentativeName.trim() || savingId === `bulk-${selectedRepresentativeSummary.responsible}`} className="rounded-2xl bg-cyan-700 px-4 py-3 text-sm font-black text-white hover:bg-cyan-800 disabled:cursor-not-allowed disabled:opacity-40" type="button">
                          Appliquer
                        </button>
                      </div>
                    </div>
                    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                      <div className="grid grid-cols-[minmax(220px,1.4fr)_150px_150px_minmax(220px,1.5fr)] gap-3 border-b border-slate-100 bg-slate-100/80 px-4 py-3 text-xs font-black uppercase tracking-wide text-slate-500">
                        <span>Client</span><span>Statut</span><span>Score client</span><span>Prochaine action</span>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {selectedRepresentativeSummary.clients.map((client) => {
                          const index = rows.indexOf(client);
                          return (
                            <button key={client.id || `${selectedRepresentativeSummary.responsible}-${client.clientName}`} type="button" onClick={() => { closeRepresentativeCard(); setSelectedIndex(index); }} style={statusRowStyle(client.status)} className={`grid w-full grid-cols-[minmax(220px,1.4fr)_150px_150px_minmax(220px,1.5fr)] gap-3 border-l-4 px-4 py-3 text-left text-sm transition ${statusRowClass(client.status)} ${statusBorderClass(client.status)}`}>
                              <span className="font-black text-slate-950">{client.clientName || 'Client sans nom'}</span>
                              <span className="flex flex-col items-start gap-1"><span className={`text-xs ${statusTextClass(client.status)}`}>{client.status}</span>{client.statusDate && <span className="text-[11px] font-bold text-slate-500">{formatStatusDate(client.status, client.statusDate)}</span>}</span>
                              <span><span className={`rounded-full border px-2 py-1 text-[11px] font-bold ${badgeClass(client.ddcScore, 'score')}`}>{client.ddcScore}</span></span>
                              <span className="line-clamp-2 text-slate-600">{client.nextStep || '—'}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {selectedRow && selectedIndex !== null && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm" onClick={() => setSelectedIndex(null)}>
                <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 12 }} onClick={(event) => event.stopPropagation()} className="max-h-[92vh] w-full max-w-6xl overflow-hidden rounded-[2rem] bg-white shadow-2xl">
                  <div className="border-b border-slate-200 bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-950 p-6 text-white">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <p className="mb-2 text-xs font-black uppercase tracking-[0.24em] text-cyan-200">Fiche client IA / Représentant BZ</p>
                        <h3 className="text-3xl font-black">{selectedRow.clientName || 'Nouveau client'}</h3>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className={`rounded-full border border-white/20 bg-white/90 px-3 py-1 text-xs ${statusTextClass(selectedRow.status)}`}>{selectedRow.status}</span>
                          {selectedRow.statusDate && <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-bold text-white">{formatStatusDate(selectedRow.status, selectedRow.statusDate)}</span>}
                          <span className={`rounded-full border px-3 py-1 text-xs font-bold ${badgeClass(selectedRow.ddcScore, 'score')}`}>{selectedRow.ddcScore}</span>
                          <span className={`rounded-full border px-3 py-1 text-xs font-bold ${badgeClass(selectedRow.approachPriority, 'priority')}`}>{selectedRow.approachPriority}</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => saveRow(selectedIndex)} disabled={savingId === (selectedRow.id || `new-${selectedIndex}`)} className="inline-flex items-center gap-2 rounded-2xl bg-emerald-400 px-4 py-2 text-sm font-black text-slate-950 hover:bg-emerald-300 disabled:opacity-50" type="button"><Save className="h-4 w-4" /> Sauvegarder</button>
                        <button onClick={() => deleteRow(selectedIndex)} className="inline-flex items-center gap-2 rounded-2xl border border-rose-300/40 bg-rose-500/15 px-4 py-2 text-sm font-bold text-rose-50 hover:bg-rose-500/25" type="button"><Trash2 className="h-4 w-4" /> Supprimer</button>
                        <button onClick={() => setSelectedIndex(null)} className="rounded-2xl border border-white/20 bg-white/10 p-2 text-white hover:bg-white/15" type="button"><X className="h-6 w-6" /></button>
                      </div>
                    </div>
                  </div>

                  <div className="max-h-[calc(92vh-144px)] overflow-y-auto bg-slate-50 p-6">
                    <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-4">
                      {[
                        { label: 'Signal client', value: `${scoreFromSignals(selectedRow)}%`, icon: Gauge },
                        { label: 'Impact affaires', value: selectedRow.ddcBusinessImpact, icon: Target },
                        { label: 'Données', value: selectedRow.ddcDataReadiness, icon: Layers3 },
                        { label: 'Décideur', value: selectedRow.ddcDecisionAccess, icon: UserRoundCheck },
                      ].map((item) => {
                        const Icon = item.icon;
                        return <div key={item.label} className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm"><Icon className="mb-3 h-5 w-5 text-cyan-700" /><p className="text-xl font-black text-slate-950">{item.value}</p><p className="text-xs font-bold uppercase tracking-wide text-slate-500">{item.label}</p></div>;
                      })}
                    </div>

                    <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
                      <div className="rounded-3xl border border-cyan-100 bg-cyan-50 p-5 lg:col-span-2">
                        <div className="mb-2 flex items-center gap-2 text-sm font-black text-cyan-950"><Sparkles className="h-4 w-4" /> Raisonnement BZ / Client</div>
                        <p className="text-sm leading-relaxed text-cyan-950">{selectedRow.ddcReasoning || selectedRow.expertAssessment || 'À compléter pendant ou après la rencontre client.'}</p>
                      </div>
                      <div className="rounded-3xl border border-rose-100 bg-rose-50 p-5">
                        <div className="mb-2 flex items-center gap-2 text-sm font-black text-rose-950"><ShieldAlert className="h-4 w-4" /> Risque à surveiller</div>
                        <p className="text-sm leading-relaxed text-rose-950">{selectedRow.riskOrCondition || 'À valider : données, confidentialité, usages IA publics, gouvernance et adoption.'}</p>
                      </div>
                    </div>

                    <div className="space-y-5">
                      {sections.map((section) => {
                        const Icon = section.icon;
                        return (
                          <section key={section.title} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                            <div className="mb-5 flex items-start gap-3">
                              <div className="rounded-2xl bg-cyan-50 p-3 text-cyan-700"><Icon className="h-5 w-5" /></div>
                              <div><h4 className="text-lg font-black text-slate-950">{section.title}</h4><p className="text-sm text-slate-500">{section.subtitle}</p></div>
                            </div>
                            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                              {section.fields.filter((field) => field.key !== 'statusDate').map((field) => (
                                <label key={field.key} className={field.type === 'textarea' ? 'lg:col-span-2' : ''}>
                                  <span className="mb-1 block text-xs font-black uppercase tracking-wide text-slate-500">{field.label}</span>
                                  {renderField(field)}
                                  {field.key === 'status' && (
                                    <div className="mt-3">
                                      <span className="mb-1 block text-xs font-black uppercase tracking-wide text-slate-500">Date liée au statut</span>
                                      {renderField({ key: 'statusDate', label: 'Date liée au statut', type: 'date' })}
                                      <p className="mt-1 text-xs font-semibold text-slate-500">Cette date suit le statut actuel : comité TI, rencontre IA, proposition, signature ou fermeture.</p>
                                    </div>
                                  )}
                                </label>
                              ))}
                            </div>
                          </section>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
